import { ReactNode, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import AppLoader from "@/components/AppLoader";
import LegalConsentGate from "@/components/legal/LegalConsentGate";
import { useMembership } from "@/hooks/useMembership";
import { useAdminRole } from "@/hooks/useAdminRole";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { RefreshCw } from "lucide-react";
import { evaluateAccessGate, PORTAL_SIGN_IN_PATH } from "@/lib/auth/accessGate";

/**
 * Gate: only paid members (or special-link members, or admins) pass.
 * Unpaid accounts are signed out and shown Access Denied.
 */
export default function RequireApproved({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { data: isAdmin, isLoading: roleLoading, isError: roleError, refetch: retryRole, isFetching: roleFetching } = useAdminRole();
  const { data: membership, isLoading: membershipLoading, isError: membershipError, refetch: retryMembership, isFetching: membershipFetching } = useMembership();

  const pendingCode = typeof window !== "undefined" ? window.localStorage.getItem("pending_founding_code") : null;
  const access = evaluateAccessGate({ hasUser: !!user, roleLoading, membershipLoading, roleError, membershipError, isAdmin: !!isAdmin, isActiveMember: !!membership?.isActive });
  const denied = access === "denied";

  useEffect(() => {
    if (!denied) return;
    if (pendingCode?.trim()) {
      navigate(`/redeem?code=${encodeURIComponent(pendingCode.trim())}`, { replace: true });
      return;
    }
    supabase.auth.signOut().finally(() => navigate("/access-denied", { replace: true }));
  }, [denied, pendingCode, navigate]);

  if (loading) return <AppLoader variant="member" />;
  if (!user) return <Navigate to={PORTAL_SIGN_IN_PATH} replace />;
  if (access === "retry") return (
    <main className="min-h-screen bg-background text-foreground flex flex-col items-center justify-center gap-4 p-6 text-center">
      <h1 className="font-display text-2xl">We couldn't confirm your access</h1>
      <p className="text-muted-foreground max-w-md">You're still signed in. Please try again when the connection returns.</p>
      <Button variant="outline" disabled={roleFetching || membershipFetching} onClick={() => { void retryRole(); void retryMembership(); }}>
        <RefreshCw className="mr-2 h-4 w-4" /> Try again
      </Button>
    </main>
  );
  if (access === "loading" || denied) return <AppLoader variant="member" />;

  return (
    <>
      <LegalConsentGate />
      {children}
    </>
  );
}
